// cells/SingleDropdownCell/SingleDropdownCell.tsx
import {
  GridRenderEditCellParams,
  useGridApiContext,
} from '@mui/x-data-grid-premium'
import { GridValidRowModel } from '@mui/x-data-grid-pro'
import { DropdownOptionModel } from 'ext-ui/components/Dropdown/components/DropdownOption/DropdownOption'
import SingleDropdown, {
  DropdownOptionGroup,
  DropdownValue,
} from 'ext-ui/components/Dropdown/SingleDropdown/SingleDropdown'
import { TextInputState } from 'ext-ui/components/TextInput/TextInput'
import { LoadingState } from 'ext-ui/hooks/useLoadingStateAnimation'
import React, { useRef } from 'react'
import DataGridCellWrapper from 'src/components/DataGrid/cells/DataGridCellWrapper/DataGridCellWrapper'
import { InfiniteScrollProps } from 'src/types'

type SingleDropdownCellProps<Row extends GridValidRowModel> = {
  params: GridRenderEditCellParams<Row, string>
  editMode: 'row' | 'cell'
  clearError?: () => void
  setValue?: (value: DropdownValue) => void
  optional?: boolean
  state?: TextInputState
  placeholder?: string
  loadingState?: LoadingState
  disabled?: boolean
  infiniteScrollProps?: InfiniteScrollProps<string>
  className?: string
  selectNone?: boolean
  searchable?: boolean
  errorText?: React.ReactElement | string
  freeTextProps?: {
    enabled: boolean
    existingOptionHeader?: string
    freeTextOptionHeader?: string
  }
  emptyMessage?: {
    icon?: React.ReactNode
    title: string
    subtitle?: string
  }
  noResultsMessage?: {
    icon?: React.ReactNode
    title: string
    subtitle?: string
  }
} & (
  | {
      options: DropdownOptionModel[]
    }
  | {
      optionGroups: DropdownOptionGroup[]
    }
)

function SingleDropdownCell<Row extends GridValidRowModel>(
  props: SingleDropdownCellProps<Row>,
): React.ReactElement {
  const { params, setValue, clearError, ...dropdownProps } = props

  const [selectedValue, setSelectedValue] = React.useState<
    DropdownValue | undefined
  >(
    params.value
      ? { value: params.value, label: params.formattedValue }
      : undefined,
  )

  const apiRef = useGridApiContext()
  const inputRef = useRef<HTMLInputElement>(null)

  const handleCancel = () => {
    if (props.editMode === 'row') {
      apiRef.current.stopRowEditMode({
        id: params.id,
        ignoreModifications: true,
      })
    } else {
      apiRef.current.stopCellEditMode({
        id: params.id,
        field: params.field,
        ignoreModifications: true,
      })
    }
  }

  const handleSelect = (selectedValue: DropdownValue) => {
    setValue?.(selectedValue)
    setSelectedValue(selectedValue)

    apiRef.current.setEditCellValue({
      id: params.id,
      field: params.field,
      value: selectedValue,
    })
    clearError?.()
    if (props.editMode === 'row') {
      apiRef.current.stopRowEditMode({
        id: params.id,
      })
    } else {
      apiRef.current.stopCellEditMode({
        id: params.id,
        field: params.field,
      })
    }
  }

  return (
    <DataGridCellWrapper params={params}>
      <SingleDropdown
        {...dropdownProps}
        variant={'small'}
        openOnMount
        ref={inputRef}
        selectedValue={selectedValue}
        onSelect={handleSelect}
        onCancel={handleCancel}
        dropdownContainer={params.api.rootElementRef.current}
        searchable={props.searchable}
        loadingState={props.loadingState}
        hideErrorMicrocopy
      />
    </DataGridCellWrapper>
  )
}

export default SingleDropdownCell
